import React, { useCallback, useEffect, useRef, useState } from 'react';
import { claimableItemsService } from '../../services/claimableItemsService';
import type { ClaimableItem, RankingCostEstimate, RankingProgress, ScoredPatent } from '../../services/claimableItemsService';
import { CAT_LABELS, CAT_COLORS, CAT_BG, btnPrimary, btnSecondary } from './claimsConstants';

export interface RankingStepProps {
  subProjectId: string;
  items: ClaimableItem[];
  onItemsChange: (items: ClaimableItem[]) => void;
  onNext: () => void;
  t: (k: string, fb?: string) => string;
}

const RankingStep: React.FC<RankingStepProps> = ({ subProjectId, items, onItemsChange, onNext, t }) => {
  const [launching, setLaunching] = useState(false);
  const [progress, setProgress] = useState<RankingProgress | null>(null);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [scoredPatents, setScoredPatents] = useState<Record<string, ScoredPatent[]>>({});
  const [loadingScores, setLoadingScores] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const [showCostConfirm, setShowCostConfirm] = useState(false);
  const [costEstimate, setCostEstimate] = useState<RankingCostEstimate | null>(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const fetchScores = useCallback(async (itemId: string) => {
    setLoadingScores(prev => new Set(prev).add(itemId));
    try {
      const scores = await claimableItemsService.getScoredPatents(subProjectId, itemId);
      setScoredPatents(prev => ({ ...prev, [itemId]: scores }));
    } catch { /* ignore */ } finally {
      setLoadingScores(prev => { const n = new Set(prev); n.delete(itemId); return n; });
    }
  }, [subProjectId]);

  const startPoll = useCallback(() => {
    stopPoll();
    pollRef.current = window.setInterval(async () => {
      try {
        const p = await claimableItemsService.getRankingProgress(subProjectId);
        setProgress(p);
        if (p.status === 'completed' || p.status === 'failed') {
          stopPoll();
          const refreshed = await claimableItemsService.list(subProjectId);
          onItemsChange(refreshed);
          const firstRanked = refreshed.find(i => i.ranking_status === 'ranked');
          if (firstRanked) { setExpandedItem(firstRanked.id); fetchScores(firstRanked.id); }
        }
      } catch { stopPoll(); }
    }, 2000);
  }, [subProjectId, onItemsChange, fetchScores]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => stopPoll(), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    claimableItemsService.getRankingProgress(subProjectId).then(p => {
      setProgress(p);
      if (p?.status === 'running') startPoll();
    }).catch(() => { /* ignore */ });
    const firstRanked = items.find(i => i.ranking_status === 'ranked');
    if (firstRanked) { setExpandedItem(firstRanked.id); fetchScores(firstRanked.id); }
  }, [subProjectId, startPoll, fetchScores]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRankAll = async () => {
    setEstimateError(null); setLoadingEstimate(true); setCostEstimate(null); setShowCostConfirm(true);
    try {
      const est = await claimableItemsService.estimateRanking(subProjectId);
      setCostEstimate(est);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail || (e as { message?: string })?.message || 'Unknown error';
      setEstimateError(detail);
    } finally { setLoadingEstimate(false); }
  };

  const handleConfirmRank = async () => {
    setShowCostConfirm(false); setError(null); setLaunching(true);
    try {
      await claimableItemsService.rankItems(subProjectId);
      startPoll();
    } catch (e: unknown) {
      setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('claimsWorkspace.step2.rankError', 'Failed to launch ranking'));
    } finally { setLaunching(false); }
  };

  const handleExpand = async (itemId: string) => {
    if (expandedItem === itemId) { setExpandedItem(null); return; }
    setExpandedItem(itemId);
    if (!scoredPatents[itemId]) await fetchScores(itemId);
  };

  const handleToggleExclude = async (itemId: string, docId: string) => {
    try {
      const r = await claimableItemsService.togglePatentExclusion(subProjectId, itemId, docId);
      setScoredPatents(prev => ({
        ...prev,
        [itemId]: (prev[itemId] || []).map(p => p.document_id === docId ? { ...p, excluded_by_user: r.excluded_by_user } : p),
      }));
    } catch { /* ignore */ }
  };

  const anyRanked = items.some(i => i.ranking_status === 'ranked');
  const isRunning = progress?.status === 'running' || launching;
  const totalCalls = items.reduce((sum, i) => sum + i.expected_claims_count, 0);

  return (
    <div style={{ padding: '12px 16px', overflowY: 'auto', height: '100%' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', flex: 1 }}>
          {t('claimsWorkspace.step2.title', 'Rank Prior Art')}
        </span>
        <button onClick={handleRankAll} disabled={isRunning || items.length === 0}
          title={t('claimsWorkspace.step2.rankAllTitle', `Scores each item against patents in KB (~${totalCalls * items.length} LLM calls)`)}
          style={{ ...btnSecondary, opacity: (isRunning || items.length === 0) ? 0.5 : 1 }}>
          {isRunning ? t('claimsWorkspace.step2.ranking', 'Ranking…') : t('claimsWorkspace.step2.rankAll', 'Rank all items')}
        </button>
        <button onClick={onNext} disabled={!anyRanked}
          style={{ ...btnPrimary, opacity: !anyRanked ? 0.4 : 1, cursor: !anyRanked ? 'default' : 'pointer' }}>
          {t('claimsWorkspace.step2.next', 'Draft claims →')}
        </button>
      </div>

      {/* Status banners */}
      {error && (
        <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 3, padding: '4px 8px', marginBottom: 8 }}>{error}</div>
      )}
      {!isRunning && progress?.status === 'completed' && (
        <div style={{ fontSize: 11, color: '#065f46', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 3, padding: '4px 8px', marginBottom: 8 }}>
          {t('claimsWorkspace.step2.rankingDone', 'Ranking complete. Click an item below to see scored patents.')}
        </div>
      )}
      {!isRunning && progress?.status !== 'completed' && anyRanked && (
        <div style={{ fontSize: 11, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 3, padding: '4px 8px', marginBottom: 8 }}>
          {t('claimsWorkspace.step2.hasRankedItems', 'Items have been ranked. Click any item row below to view scored patents.')}
        </div>
      )}

      {/* Progress bar */}
      {isRunning && progress && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#64748b', marginBottom: 3 }}>
            <span>{progress.current_item || t('claimsWorkspace.step2.processing', 'Processing…')}</span>
            <span>{progress.items_completed ?? progress.items_done ?? 0} / {progress.items_total ?? items.length}</span>
          </div>
          <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#2563eb', transition: 'width 0.5s',
              width: `${progress.items_total ? Math.round(((progress.items_completed ?? progress.items_done ?? 0) / progress.items_total) * 100) : 0}%` }} />
          </div>
        </div>
      )}

      {/* Item accordion */}
      {items.map(item => {
        const isOpen = expandedItem === item.id;
        const scores = scoredPatents[item.id] || [];
        const isLoading = loadingScores.has(item.id);
        return (
          <div key={item.id} style={{ border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 6, overflow: 'hidden' }}>
            <div onClick={() => handleExpand(item.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', background: '#f8fafc', cursor: 'pointer' }}>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{isOpen ? '▼' : '▶'}</span>
              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: CAT_BG[item.category] ?? '#e0e7ff', color: CAT_COLORS[item.category] ?? '#3730a3', fontWeight: 700, textTransform: 'uppercase' }}>
                {CAT_LABELS[item.category] ?? item.category}
              </span>
              <span style={{ fontSize: 11, fontWeight: 500, color: '#1e293b', flex: 1 }}>{item.label}</span>
              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 600,
                background: item.ranking_status === 'ranked' ? '#d1fae5' : item.ranking_status === 'ranking' ? '#fef9c3' : '#f1f5f9',
                color: item.ranking_status === 'ranked' ? '#065f46' : item.ranking_status === 'ranking' ? '#92400e' : '#94a3b8' }}>
                {item.ranking_status}
              </span>
            </div>
            {isOpen && (
              <div style={{ padding: '6px 8px', background: '#fff' }}>
                {isLoading && <div style={{ fontSize: 11, color: '#94a3b8' }}>{t('common.loading', 'Loading…')}</div>}
                {!isLoading && scores.length === 0 && (
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {item.ranking_status === 'pending' ? t('claimsWorkspace.step2.notRankedYet', 'Not ranked yet.') : t('claimsWorkspace.step2.noScores', 'No scored patents found.')}
                  </div>
                )}
                {!isLoading && scores.length > 0 && (
                  <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ color: '#64748b', background: '#f8fafc' }}>
                        <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #e2e8f0' }}>{t('claimsWorkspace.step2.colScore', 'Score')}</th>
                        <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #e2e8f0' }}>{t('claimsWorkspace.step2.colPatent', 'Patent')}</th>
                        <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #e2e8f0' }}>{t('claimsWorkspace.step2.colRationale', 'Rationale')}</th>
                        <th style={{ textAlign: 'center', padding: '3px 6px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{t('claimsWorkspace.step2.colExclude', 'Exclude')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scores.map(p => (
                        <tr key={p.document_id} style={{ opacity: p.excluded_by_user ? 0.4 : 1, borderBottom: '1px solid #f8fafc' }}>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <span style={{ display: 'inline-block', width: 20, height: 20, lineHeight: '20px', textAlign: 'center', borderRadius: '50%', fontSize: 10, fontWeight: 700,
                              background: p.score >= 3 ? '#d1fae5' : p.score >= 2 ? '#fef9c3' : '#f1f5f9',
                              color: p.score >= 3 ? '#065f46' : p.score >= 2 ? '#92400e' : '#94a3b8' }}>
                              {p.score}
                            </span>
                          </td>
                          <td style={{ padding: '3px 6px', fontWeight: 500, color: '#1e293b' }}>
                            {p.patent_id || p.document_id.slice(0, 12)}
                            {p.title && <div style={{ fontSize: 9, color: '#64748b', fontWeight: 400 }}>{p.title.slice(0, 60)}{p.title.length > 60 ? '…' : ''}</div>}
                          </td>
                          <td style={{ padding: '3px 6px', color: '#475569', fontStyle: 'italic' }}>
                            {p.rationale?.slice(0, 100)}{(p.rationale?.length ?? 0) > 100 ? '…' : ''}
                          </td>
                          <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                            <input type="checkbox" checked={p.excluded_by_user} onChange={() => handleToggleExclude(item.id, p.document_id)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Cost confirmation modal */}
      {showCostConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCostConfirm(false); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-base font-semibold">{t('claimsWorkspace.step2.costTitle', 'Ranking — Cost Estimate')}</h2>
              <span className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer" onClick={() => setShowCostConfirm(false)}>&times;</span>
            </div>
            <div className="px-5 py-4 space-y-4">
              {loadingEstimate && <p className="text-sm text-slate-500 text-center py-4">{t('claimsWorkspace.step2.estimating', 'Estimating cost…')}</p>}
              {estimateError && <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{estimateError}</p>}
              {costEstimate && !loadingEstimate && (
                <div className="bg-slate-50 rounded p-3 space-y-1.5 text-sm">
                  {[
                    [t('claimsWorkspace.step2.costItems', 'Items to rank'), costEstimate.items_to_rank],
                    [t('claimsWorkspace.step2.costPatents', 'Patents in KB'), costEstimate.patent_count],
                    [t('claimsWorkspace.step2.costLlmCalls', 'Estimated LLM calls'), costEstimate.estimated_llm_calls.toLocaleString()],
                  ].map(([label, val]) => (
                    <div key={String(label)} className="flex justify-between">
                      <span className="text-slate-600">{label}</span>
                      <span className="font-medium">{val}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{t('claimsWorkspace.step2.costTokensPerCall', 'Tokens per call (in / out)')}</span>
                    <span>~{costEstimate.tokens_per_call_input} / ~{costEstimate.tokens_per_call_output}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-1.5 mt-1.5 flex justify-between">
                    <span className="text-slate-700 font-medium">{t('claimsWorkspace.step2.costEstimated', 'Estimated cost')}</span>
                    <span className="font-bold text-base">
                      {costEstimate.currency === 'EUR' ? '€' : '$'}
                      {costEstimate.estimated_cost_cents < 1
                        ? (costEstimate.estimated_cost_cents / 100).toFixed(4)
                        : (costEstimate.estimated_cost_cents / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-slate-500 text-xs">
                    <span>{t('claimsWorkspace.step2.costModel', 'Model')}</span>
                    <span>{costEstimate.model_name || costEstimate.model_id}</span>
                  </div>
                  {costEstimate.has_cache_note && (
                    <p className="text-xs text-slate-400 pt-1">{t('claimsWorkspace.step2.costCacheNote', 'Previously scored (item, patent) pairs are cached and will not be re-billed.')}</p>
                  )}
                  <p className="text-xs text-slate-400">{t('claimsWorkspace.step2.costDisclaimer', 'This is an estimate. Actual cost depends on patent claim lengths.')}</p>
                </div>
              )}
              {costEstimate && costEstimate.patent_count === 0 && !loadingEstimate && (
                <p className="text-sm text-amber-700 bg-amber-50 rounded px-3 py-2">
                  {t('claimsWorkspace.step2.noPatents', 'No patents found in the attached KB. Ranking cannot proceed.')}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
              <button onClick={() => setShowCostConfirm(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded hover:bg-slate-50">
                {t('common.cancel', 'Cancel')}
              </button>
              <button onClick={handleConfirmRank}
                disabled={loadingEstimate || !!estimateError || !costEstimate || costEstimate.patent_count === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {t('claimsWorkspace.step2.confirmRank', 'Confirm & rank')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RankingStep;
